package com.partejar.app.core.ui.components

import androidx.compose.animation.core.CubicBezierEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.animation.core.rememberTransition
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.snap
import androidx.compose.foundation.layout.Box
import androidx.compose.ui.platform.LocalDensity
import com.partejar.app.core.ui.theme.PartejarTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.MotionDurationScale
import androidx.compose.ui.graphics.graphicsLayer

/** motion-design: Premium press feedback, mobile timing, no overshoot. Opt-in for Exames. */
internal object PartejarMotion {
    val calm = CubicBezierEasing(0.4f, 0f, 0.2f, 1f)
    val enter = CubicBezierEasing(0f, 0f, 0.2f, 1f)
    val exit = CubicBezierEasing(0.4f, 0f, 1f, 1f)
    const val press = 80
    const val release = 150
    const val feedback = 200
    const val entrance = 320
    const val departure = 220
    const val skeletonPulse = 1400
}

@Composable
internal fun reducedMotion(): Boolean =
    (rememberCoroutineScope().coroutineContext[MotionDurationScale]?.scaleFactor ?: 1f) == 0f

@Composable
internal fun Modifier.partejarPressFeedback(pressed: Boolean, enabled: Boolean): Modifier {
    val reduced = reducedMotion()
    val scale by animateFloatAsState(
        if (pressed && !reduced) 0.98f else 1f,
        tween(if (pressed) PartejarMotion.press else PartejarMotion.release, easing = PartejarMotion.calm),
        label = "partejarPress"
    )
    val alpha by animateFloatAsState(
        if (!enabled) 0.6f else if (pressed) 0.9f else 1f,
        tween(PartejarMotion.feedback, easing = PartejarMotion.calm), label = "partejarState"
    )
    return graphicsLayer { scaleX = if (reduced) 1f else scale; scaleY = scaleX; this.alpha = alpha }
}

/** Retain measured content through exit. Collapsing a dialog before dismissal recenters its window. */
@Composable
internal fun PartejarVisibility(state: MutableTransitionState<Boolean>, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    val reduced = reducedMotion()
    val distance = with(LocalDensity.current) { PartejarTheme.spacing.space12.toPx() }
    val transition = rememberTransition(state, label = "partejarVisibility")
    val progress by transition.animateFloat(transitionSpec = {
        if (reduced) snap() else tween(
            if (targetState) PartejarMotion.entrance else PartejarMotion.departure,
            easing = if (targetState) PartejarMotion.enter else PartejarMotion.exit)
    }, label = "partejarVisibilityProgress") { visible -> if (visible) 1f else 0f }
    Box(modifier.graphicsLayer {
        alpha = progress
        translationY = if (reduced) 0f else distance * (1f - progress)
    }) { content() }
}
